import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AOS from "aos";
import "aos/dist/aos.css";
import FormError from "./FormError";

const ChangePasswordPage = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";

  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";

  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  AOS.init({ duration: 700 });

  const resetPassword = async (resetData) => {
    try {
      const response = await axiosInstance.post(
        "/api/v1/accounts/reset-password",
        {
          email: resetData.email,
          password: resetData.password,
          confirmPassword: resetData.confirmPassword,
          code: resetData.code,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();
    setErrors({});

    const newErrors = {};

    if (!verificationCode)
      newErrors.verificationCode = t("ChangePassword.emptyCode");
    if (!password) newErrors.password = t("ChangePassword.emptyPassword");
    if (!confirmPassword)
      newErrors.confirmPassword = t("ChangePassword.emptyConfirmPassword");
    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = t("ChangePassword.passwordMismatch");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const response = await resetPassword({
        email,
        password,
        confirmPassword,
        code: verificationCode,
      });

      toast.success(t("ChangePassword.successMessage"));
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      const errorMessage =
        error?.detail ||
        error?.title ||
        error?.message ||
        t("ChangePassword.errorMessage") ||
        "An error occurred.";

      setErrors({ general: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="rbt-header-sticky rbt-header-transpernt-active active-light-mode"
      dir={languageDirection}
    >
      <div className="container">
        <section className="py-5">
          <div className="container py-4">
            <div className="row justify-content-center">
              <div className="col-lg-5">
                <div className="card border-0 shadow-sm" data-aos="fade-up">
                  <div className="card-body p-4 p-md-5">
                    <h2 className="text-center fw-bold text-primary-custom mb-4">
                      {t("ChangePassword.title")}
                    </h2>
                    <p className="text-muted text-center mb-4">
                      {t("ChangePassword.instruction")}
                    </p>

                    {errors.general && (
                      <FormError errorMessage={errors.general} />
                    )}

                    <form onSubmit={handleSubmit}>
                      <div
                        className="form-group mb-3"
                        data-aos="fade-up"
                        data-aos-delay="200"
                      >
                        <label>{t("ChangePassword.emailLabel")}</label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.verificationCode ? "is-invalid" : ""
                          }`}
                          value={email}
                          readOnly
                        />
                      </div>

                      <div
                        className="form-group mb-3"
                        data-aos="fade-up"
                        data-aos-delay="300"
                      >
                        <label>{t("ChangePassword.codeLabel")}</label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.verificationCode ? "is-invalid" : ""
                          }`}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                        />
                        <FormError errorMessage={errors.verificationCode} />
                      </div>

                      <div
                        className="form-group mb-3"
                        data-aos="fade-up"
                        data-aos-delay="400"
                      >
                        <label>{t("ChangePassword.passwordLabel")}</label>
                        <input
                          type="password"
                          className={`form-control ${
                            errors.verificationCode ? "is-invalid" : ""
                          }`}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <FormError errorMessage={errors.password} />
                      </div>

                      <div
                        className="form-group mb-3"
                        data-aos="fade-up"
                        data-aos-delay="500"
                      >
                        <label>
                          {t("ChangePassword.confirmPasswordLabel")}
                        </label>
                        <input
                          type="password"
                          className={`form-control ${
                            errors.verificationCode ? "is-invalid" : ""
                          }`}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                        <FormError errorMessage={errors.confirmPassword} />
                      </div>

                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={isLoading}
                      >
                        {isLoading
                          ? t("ChangePassword.loading")
                          : t("ChangePassword.submitButton")}
                      </button>
                    </form>

                    <div className="text-center mt-3">
                      <p>
                        {t("ChangePassword.backToLogin")}{" "}
                        <a href="/login" className="auth-link">
                          {t("ChangePassword.loginLink")}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        <ToastContainer autoClose={5000} />
      </div>
    </div>
  );
};

export default ChangePasswordPage;
